import {
  getPropertyType,
  isVendorProperty,
  useVhalGrpcClient,
  VhalPropertyChangeMode,
  VhalPropertyType,
  type VhalPropertyValue,
} from "../../index";
import { useVhalSubscription, valueKey, type CatalogEntry } from "../../vehicle";
import { IconDownload, IconEye, IconEyeOff, IconSearch } from "@repo/ui/icons";
import { toast } from "@repo/ui/na";
import { useEffect, useMemo, useState } from "react";
import classes from "../emulatorView.module.scss";

type Filter = "writable" | "zoned" | "vendor" | "watched";
const FILTERS: [Filter, string][] = [
  ["writable", "Writable"],
  ["zoned", "Zoned"],
  ["vendor", "Vendor"],
  ["watched", "Watched"],
];

const TYPE: Partial<Record<VhalPropertyType, string>> = {
  [VhalPropertyType.STRING]: "string",
  [VhalPropertyType.BOOLEAN]: "bool",
  [VhalPropertyType.INT32]: "int32",
  [VhalPropertyType.INT32_VEC]: "int32[]",
  [VhalPropertyType.INT64]: "int64",
  [VhalPropertyType.INT64_VEC]: "int64[]",
  [VhalPropertyType.FLOAT]: "float",
  [VhalPropertyType.FLOAT_VEC]: "float[]",
  [VhalPropertyType.BYTES]: "bytes",
  [VhalPropertyType.MIXED]: "mixed",
};
const CHANGE: Record<number, string> = {
  [VhalPropertyChangeMode.STATIC]: "static",
  [VhalPropertyChangeMode.ON_CHANGE]: "on change",
  [VhalPropertyChangeMode.CONTINUOUS]: "continuous",
};
const hex = (n: number) => `0x${n.toString(16)}`;
const access = (e: CatalogEntry) => (e.readable && e.writable ? "read/write" : e.writable ? "write" : "read");

export function formatValue(e: CatalogEntry, v?: VhalPropertyValue): string {
  if (!v) return "—";
  const t = getPropertyType(e.id);
  if (t === VhalPropertyType.BOOLEAN) return v.int32Values?.[0] ? "true" : "false";
  if (t === VhalPropertyType.STRING) return JSON.stringify(v.stringValue ?? "");
  if (t === VhalPropertyType.BYTES) return `${v.byteValues?.length ?? 0} bytes`;
  if (t === VhalPropertyType.INT32 && e.enumValues) {
    const n = v.int32Values?.[0];
    return n == null ? "—" : `${e.enumValues[n] ?? n} (${n})`;
  }
  const nums = [...(v.floatValues ?? []), ...(v.int32Values ?? []), ...(v.int64Values ?? []).map(Number)];
  if (!nums.length) return "—";
  const s = nums.map((x) => (Number.isInteger(x) ? String(x) : x.toFixed(2))).join(", ");
  return e.units ? `${s} ${e.units}` : s;
}

/** Parse editor text into a typed value; returns an error message instead of ever sending NaN. */
function parse(e: CatalogEntry, areaId: number, text: string): VhalPropertyValue | string {
  const t = getPropertyType(e.id);
  const base = { propertyId: e.id, areaId };
  const area = e.areas.find((a) => a.areaId === areaId);
  const nums = text.split(/[\s,]+/).filter(Boolean).map(Number);
  if (t === VhalPropertyType.BOOLEAN) {
    if (!/^(true|false|1|0)$/i.test(text.trim())) return "Enter true or false";
    return { ...base, int32Values: [/^(true|1)$/i.test(text.trim()) ? 1 : 0] };
  }
  if (t === VhalPropertyType.STRING) return { ...base, stringValue: text };
  if (t === VhalPropertyType.BYTES) {
    const clean = text.replace(/\s|0x/gi, "");
    if (!/^([0-9a-f]{2})*$/i.test(clean) || clean.length / 2 > 256) return "Enter up to 256 hex bytes";
    return { ...base, byteValues: new Uint8Array(clean.match(/../g)?.map((b) => parseInt(b, 16)) ?? []) };
  }
  if (t === VhalPropertyType.MIXED) return "Mixed values are read-only here";
  if (!nums.length || nums.some((n) => Number.isNaN(n))) return "Enter a number";
  const vec = t === VhalPropertyType.INT32_VEC || t === VhalPropertyType.FLOAT_VEC || t === VhalPropertyType.INT64_VEC;
  if (!vec && nums.length > 1) return "Enter a single value";
  if (area?.supportedValues && !vec && !area.supportedValues.includes(nums[0]!)) return `Supported: ${area.supportedValues.join(", ")}`;
  if (area?.min != null && area.max != null && area.max > area.min && nums.some((n) => n < area.min! || n > area.max!))
    return `Range ${area.min} – ${area.max}`;
  if (t === VhalPropertyType.FLOAT || t === VhalPropertyType.FLOAT_VEC) return { ...base, floatValues: nums };
  if (t === VhalPropertyType.INT64 || t === VhalPropertyType.INT64_VEC) return { ...base, int64Values: nums.map((n) => BigInt(Math.round(n))) };
  if (nums.some((n) => !Number.isInteger(n))) return "Enter an integer";
  return { ...base, int32Values: nums };
}

function editText(e: CatalogEntry, v?: VhalPropertyValue) {
  if (!v) return "";
  const t = getPropertyType(e.id);
  if (t === VhalPropertyType.BOOLEAN) return v.int32Values?.[0] ? "true" : "false";
  if (t === VhalPropertyType.STRING) return v.stringValue ?? "";
  if (t === VhalPropertyType.BYTES) return [...(v.byteValues ?? [])].map((b) => b.toString(16).padStart(2, "0")).join(" ");
  return [...(v.floatValues ?? []), ...(v.int32Values ?? []), ...(v.int64Values ?? []).map(Number)].join(", ");
}

export function InspectorTool({ emulatorId }: { emulatorId: string }) {
  const sub = useVhalSubscription();
  const client = useVhalGrpcClient(emulatorId);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState<Filter[]>([]);
  const [sel, setSel] = useState<string | null>(null);

  const rows = useMemo(
    () =>
      [...sub.catalog.values()].flatMap((e) => e.areas.map((a) => ({ e, areaId: a.areaId, key: valueKey(e.id, a.areaId) }))),
    [sub.catalog],
  );
  const needle = q.trim().toLowerCase();
  const shown = rows.filter(({ e, areaId }) => {
    if (needle && !`${e.name} ${hex(e.id)}`.toLowerCase().includes(needle)) return false;
    if (filters.includes("writable") && !e.writable) return false;
    if (filters.includes("zoned") && areaId === 0) return false;
    if (filters.includes("vendor") && !isVendorProperty(e.id)) return false;
    if (filters.includes("watched") && !sub.watched.includes(e.id)) return false;
    return true;
  });
  const selected = rows.find((r) => r.key === sel) ?? shown[0];
  const [edit, setEdit] = useState("");
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (selected) setEdit(editText(selected.e, sub.values.get(selected.key)));
    setErr(null);
    // prefill only when the selection changes
  }, [selected?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const dump = async () => {
    try {
      const res = await client.dump({ options: [] });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([res.buffer], { type: "text/plain" }));
      a.download = `vhal-dump-${emulatorId.slice(0, 8)}.txt`;
      a.click();
      toast("VHAL dump saved", a.download);
    } catch (e) {
      toast("VHAL dump failed", e instanceof Error ? e.message : String(e), "danger");
    }
  };

  if (!sub.available && !sub.discovering) {
    return (
      <div style={{ padding: 40, textAlign: "center", fontSize: 13, color: "var(--na-fg-muted)" }}>
        VHAL not available · Ensure the emulator is connected and the car service is running
      </div>
    );
  }

  return (
    <div style={{ display: "flex", minHeight: "100%", flexWrap: "wrap" }}>
      <div style={{ flex: "1 1 520px", minWidth: 0, display: "flex", flexDirection: "column" }}>
        <div className={classes.toolStrip}>
          <label className={classes.search}>
            <IconSearch size={14} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or hex ID" aria-label="Search properties" />
          </label>
          {FILTERS.map(([k, l]) => (
            <button
              key={k}
              type="button"
              className={classes.pill}
              aria-pressed={filters.includes(k)}
              onClick={() => setFilters((f) => (f.includes(k) ? f.filter((x) => x !== k) : [...f, k]))}
            >
              {l}
            </button>
          ))}
          <span className={classes.mono11} style={{ marginLeft: "auto" }}>
            {shown.length} of {rows.length}
          </span>
          <button type="button" className={classes.pill} onClick={dump} disabled={sub.transport !== "grpc"} title={sub.transport !== "grpc" ? "Dump needs the gRPC transport" : undefined}>
            <IconDownload size={14} />
            Dump
          </button>
        </div>
        <div style={{ overflowX: "auto" }}>
          <div style={{ minWidth: 760 }} role="table" aria-label="VHAL properties">
            <div className={classes.propRow} data-head role="row">
              <span>ID</span>
              <span>Property</span>
              <span>Type</span>
              <span>Access</span>
              <span>Change</span>
              <span>Area</span>
              <span>Live value</span>
              <span />
            </div>
            {shown.map(({ e, areaId, key }) => {
              const watched = sub.watched.includes(e.id);
              return (
                <div
                  key={key}
                  role="row"
                  tabIndex={0}
                  className={classes.propRow}
                  data-selected={selected?.key === key || undefined}
                  onClick={() => setSel(key)}
                  onKeyDown={(ev) => ev.key === "Enter" && setSel(key)}
                >
                  <span className="na-mono" style={{ color: "var(--na-fg-muted)" }}>{hex(e.id)}</span>
                  <span className="na-mono">{e.name}</span>
                  <span style={{ color: "var(--na-fg-dimmed)" }}>{TYPE[getPropertyType(e.id)] ?? "?"}</span>
                  <span style={{ color: "var(--na-fg-dimmed)" }}>{access(e)}</span>
                  <span style={{ color: "var(--na-fg-dimmed)" }}>{CHANGE[e.changeMode] ?? "?"}</span>
                  <span className="na-mono" style={{ color: "var(--na-fg-dimmed)", fontSize: 11 }}>{areaId ? hex(areaId) : "GLOBAL"}</span>
                  <span className="na-mono">{formatValue(e, sub.values.get(key))}</span>
                  <button
                    type="button"
                    aria-label={watched ? "Stop watching on timeline" : "Watch on timeline"}
                    title={watched ? "Stop watching" : "Watch on timeline"}
                    aria-pressed={watched}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      sub.toggleWatch(e.id);
                    }}
                    style={{ width: 24, height: 24, display: "grid", placeItems: "center", background: "none", border: 0, cursor: "pointer", color: watched ? "var(--na-sky-4)" : "var(--na-fg-muted)" }}
                  >
                    {watched ? <IconEye size={14} /> : <IconEyeOff size={14} />}
                  </button>
                </div>
              );
            })}
            {shown.length === 0 && <div style={{ padding: 24, textAlign: "center", fontSize: 13, color: "var(--na-fg-muted)" }}>No properties match</div>}
          </div>
        </div>
      </div>
      {selected && (
        <aside className={classes.side} aria-label="Property details">
          <div>
            <div style={{ font: "600 13px/18px var(--na-font-mono)", wordBreak: "break-all" }}>{selected.e.name}</div>
            <div className={classes.mono11} style={{ lineHeight: "16px" }}>
              {hex(selected.e.id)} · {selected.areaId ? hex(selected.areaId) : "GLOBAL"} · {selected.e.source === "device" ? "device config" : "static catalog"}
            </div>
          </div>
          <div className={classes.kv}>
            <span>Type</span>
            <span>{TYPE[getPropertyType(selected.e.id)]}</span>
            <span>Access</span>
            <span>{access(selected.e)}</span>
            <span>Change</span>
            <span>{CHANGE[selected.e.changeMode]}</span>
            {(() => {
              const a = selected.e.areas.find((x) => x.areaId === selected.areaId);
              return a?.min != null || a?.supportedValues ? (
                <>
                  <span>Range</span>
                  <span>{a.supportedValues ? a.supportedValues.join(", ") : `${a.min} – ${a.max}`}</span>
                </>
              ) : null;
            })()}
            <span>Status</span>
            <span style={{ color: (sub.values.get(selected.key)?.status ?? 0) === 0 ? "var(--na-success)" : "var(--na-warning)" }}>
              {["AVAILABLE", "UNAVAILABLE", "ERROR"][sub.values.get(selected.key)?.status ?? 0]}
            </span>
          </div>
          <div style={{ border: "1px solid var(--na-border)", padding: "10px 12px" }}>
            <div style={{ fontSize: 11, color: "var(--na-fg-muted)", marginBottom: 4 }}>Current value</div>
            <div style={{ font: "500 18px/24px var(--na-font-mono)", overflowWrap: "anywhere" }}>{formatValue(selected.e, sub.values.get(selected.key))}</div>
          </div>
          {selected.e.writable && getPropertyType(selected.e.id) === VhalPropertyType.BOOLEAN ? (
            // Booleans switch in one click instead of typing true or false.
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div role="radiogroup" aria-label="Set value" className={classes.boolSeg}>
                {[false, true].map((b) => {
                  const on = Boolean(sub.values.get(selected.key)?.int32Values?.[0]) === b;
                  return (
                    <button
                      key={String(b)}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={async () => {
                        if (on) return;
                        setErr(null);
                        const res = await sub.write(
                          [{ prop: selected.e.id, areaId: selected.areaId, value: { propertyId: selected.e.id, areaId: selected.areaId, int32Values: [b ? 1 : 0] } }],
                          selected.e.name,
                        );
                        if (!res.ok) setErr(res.errors[0] ?? "Write rejected");
                      }}
                    >
                      {b ? "True" : "False"}
                    </button>
                  );
                })}
              </div>
              {err && <div style={{ fontSize: 12, color: "var(--na-danger-text)" }}>{err}</div>}
            </div>
          ) : selected.e.writable ? (
            <form
              style={{ display: "flex", flexDirection: "column", gap: 6 }}
              onSubmit={async (ev) => {
                ev.preventDefault();
                const v = parse(selected.e, selected.areaId, edit);
                if (typeof v === "string") return setErr(v);
                setErr(null);
                const res = await sub.write([{ prop: selected.e.id, areaId: selected.areaId, value: v }], selected.e.name);
                if (!res.ok) setErr(res.errors[0] ?? "Write rejected");
              }}
            >
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  value={edit}
                  onChange={(e) => setEdit(e.target.value)}
                  placeholder="New value"
                  aria-label="New value"
                  aria-invalid={Boolean(err)}
                  style={{ flex: 1, minWidth: 0, height: 30, padding: "0 8px", background: "var(--na-surface)", border: "1px solid var(--na-border)", outline: "none", color: "var(--na-fg)", font: "12px var(--na-font-mono)" }}
                />
                <button type="submit" style={{ height: 30, padding: "0 12px", background: "var(--na-primary)", color: "var(--na-primary-contrast)", border: 0, font: "500 12px/1 var(--na-font-sans)", cursor: "pointer" }}>
                  Set
                </button>
              </div>
              {err && <div style={{ fontSize: 12, color: "var(--na-danger-text)" }}>{err}</div>}
            </form>
          ) : (
            <div style={{ fontSize: 12, color: "var(--na-fg-muted)" }}>Read-only on this emulator</div>
          )}
          <div>
            <div style={{ fontSize: 11, color: "var(--na-fg-muted)", marginBottom: 6 }}>Watched on timeline</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {sub.watched.map((p) => (
                <span key={p} className="na-mono" style={{ fontSize: 11, padding: "4px 6px", border: "1px solid var(--na-border)" }}>
                  {sub.catalog.get(p)?.name ?? hex(p)}
                </span>
              ))}
            </div>
          </div>
        </aside>
      )}
    </div>
  );
}
